import { forwardRef, useEffect, useState, type FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Input, cn } from '@/design-system';
import { formatMoney, isDecimalString, parseMoneyInput, toEditable } from '@/shared/lib/money';

export interface MoneyInputProps {
  id?: string;
  name?: string;
  /** Decimal string ("250000.00") or null when empty. */
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  /** Called with the raw text when it can't be parsed, so the form can show an error. */
  onInvalid?: (raw: string) => void;
  allowNegative?: boolean;
  currency?: 'AED';
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-label'?: string;
}

/**
 * The only way to enter money. Value is a decimal string; the component never uses Number().
 * Accepts "250,000", "250k", "1.2m"; formats with grouping on blur; left-to-right in RTL.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onChange, onBlur, onInvalid, allowNegative = false, currency = 'AED', className, placeholder = '0.00', ...rest },
  ref,
) {
  const { t } = useTranslation();
  const display = (v: string | null | undefined) => (v && isDecimalString(v) ? formatMoney(v) : '');
  const [text, setText] = useState(() => display(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(display(value));
  }, [value, focused]);

  const commit = (raw: string) => {
    const parsed = parseMoneyInput(raw, { allowNegative });
    if (parsed.ok) {
      onChange(parsed.value);
      setText(formatMoney(parsed.value));
    } else if (parsed.reason === 'empty') {
      onChange(null);
      setText('');
    } else {
      onInvalid?.(raw);
    }
  };

  return (
    <Input
      ref={ref}
      {...rest}
      dir="ltr"
      inputMode="decimal"
      autoComplete="off"
      placeholder={placeholder}
      startAdornment={
        <span>
          <span aria-hidden="true">{currency}</span>
          <span className="sr-only">{t('money.currency')}</span>
        </span>
      }
      className={cn('[&_input]:tabular [&_input]:text-end', className)}
      value={text}
      onFocus={(e: FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        const editable = toEditable(value ?? '');
        // Show the plain value while editing and keep it selected, so typing replaces it (never appends to it).
        e.currentTarget.value = editable;
        e.currentTarget.select();
        setText(editable);
      }}
      onChange={(e) => setText(e.target.value)}
      onBlur={(e: FocusEvent<HTMLInputElement>) => {
        setFocused(false);
        commit(e.target.value);
        onBlur?.();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
      }}
    />
  );
});
